/*
  Suit Heritage Customizer 3.0 — multipage flow + measurement guidance
  Native <dialog> product customizer for the current sh-customizer Liquid block.

  Responsibilities:
  - Render the JSON schema embedded by product-block-suit-customizer.liquid.
  - Keep Full Customization and Tailoring Only in one accessible drawer.
  - Persist selections across theme re-renders in the current page session.
  - Write private Shopify line-item properties to the real product form.
  - Coordinate standard/custom sizing with the sibling <sh-size> component.
  - Keep touch/pointer handling native; no synthetic overlay or z-index hacks.
*/
(() => {
  'use strict';

  if (customElements.get('sh-customizer')) return;

  const MEMORY = new Map();
  const OWNED = 'shcOwned';
  const PROPERTY_PREFIX = '_Suit Heritage · ';
  const MEASUREMENT_FIELD_IDS = new Set([
    'chest',
    'sleeves',
    'shoulder',
    'jacket_length',
    'bicep',
    'stomach',
    'pant_inseam',
    'pant_outseam',
    'waist',
    'seat',
    'front_rise',
    'back_rise',
    'thighs',
    'knee',
    'leg_opening'
  ]);

  const MEASUREMENT_GUIDES = {
    chest: {
      title: 'Chest',
      steps: [
        'Wrap the tape around the fullest part of your chest, under your arms and across your shoulder blades.',
        'Keep the tape horizontal to the floor and comfortably close to the body.',
        'Stand naturally and breathe normally. Do not expand your chest.'
      ]
    },
    sleeves: {
      title: 'Sleeve Length',
      steps: [
        'Start at the shoulder point where the shoulder seam would sit.',
        'Run the tape down the outside of the arm, over a slightly bent elbow, to the wrist bone.',
        'Keep your arm relaxed rather than fully straight.'
      ]
    },
    shoulder: {
      title: 'Shoulder',
      steps: [
        'Measure across the back from one shoulder point to the other.',
        'Follow the natural line where a jacket shoulder seam should sit.',
        'Keep the tape straight without wrapping it around the shoulders.'
      ]
    },
    jacket_length: {
      title: 'Jacket Length',
      steps: [
        'Begin beside the neck at the highest shoulder point.',
        'Measure straight down the front of the body to the desired jacket hem.',
        'Stand upright and keep the tape vertical.'
      ]
    },
    bicep: {
      title: 'Bicep',
      steps: [
        'Let your arm hang naturally at your side.',
        'Wrap the tape around the fullest part of your upper arm.',
        'Keep the tape comfortably close without flexing your arm.'
      ]
    },
    stomach: {
      title: 'Stomach',
      steps: [
        'Wrap the tape around the fullest part of your stomach, usually close to the navel.',
        'Keep the tape horizontal to the floor.',
        'Stand naturally and do not pull your stomach in.'
      ]
    },
    pant_inseam: {
      title: 'Pant Inseam',
      steps: [
        'Wear your trousers at your normal waist position, or use well-fitting trousers as a reference.',
        'Start at the crotch seam and follow the inside of the leg down to the desired finished hem.',
        'Keep the tape straight without stretching the fabric.'
      ]
    },
    pant_outseam: {
      title: 'Pant Outseam',
      steps: [
        'Start at the top of the trouser waistband at the side of your body.',
        'Measure straight down the outside of the leg to your preferred finished trouser hem.',
        'Stand upright and keep the tape vertical.'
      ]
    },
    waist: {
      title: 'Waist',
      steps: [
        'Wrap the tape around the point where you normally wear your suit trousers.',
        'Keep the tape level all the way around your body.',
        'Measure comfortably — do not pull the tape tight.'
      ]
    },
    seat: {
      title: 'Hips',
      steps: [
        'Empty your pockets and wrap your measuring tape around the widest part of your buttocks.',
        'Make sure that your tape is parallel and level to the floor.'
      ]
    },
    front_rise: {
      title: 'Front Rise',
      steps: [
        'Start at the top of the front waistband where your trousers naturally sit.',
        'Measure down to the crotch seam between the legs.',
        'Keep the tape close to the body without pulling it tight.'
      ]
    },
    back_rise: {
      title: 'Back Rise',
      steps: [
        'Start at the top of the back waistband where your trousers naturally sit.',
        'Follow the body down to the crotch seam between the legs.',
        'Keep the tape smooth against the body without stretching it.'
      ]
    },
    thighs: {
      title: 'Thigh',
      steps: [
        'Stand naturally with your weight evenly distributed.',
        'Wrap the tape around the fullest part of the upper thigh, just below the crotch.',
        'Keep the tape level and comfortably close.'
      ]
    },
    knee: {
      title: 'Knee',
      steps: [
        'Stand with the leg relaxed and straight.',
        'Wrap the tape around the widest part of the knee.',
        'Do not pull the tape tight into the leg.'
      ]
    },
    leg_opening: {
      title: 'Leg Opening',
      steps: [
        'Use a well-fitting pair of trousers as your reference where possible.',
        'Measure around the hem opening at the bottom of the trouser leg.',
        'Keep the measurement relaxed so the opening is not pulled or stretched.'
      ]
    }
  };

  // HTML numeric inputs permit scientific notation and ignore maxlength.
  // Text inputs with a decimal keyboard let us apply the same MTM rule as Etsy:
  // 1–2 digits before the decimal, optionally 1–2 after; zero is not a measurement.
  const MEASUREMENT_DRAFT = /^\d{0,2}(?:\.\d{0,2})?$/;
  const MEASUREMENT_FINAL = /^\d{1,2}(?:\.\d{1,2})?$/;
  const validMeasurement = (value) =>
    MEASUREMENT_FINAL.test(String(value)) && Number(value) > 0;

  const clone = (value) => {
    try {
      return JSON.parse(JSON.stringify(value));
    } catch (_) {
      return value;
    }
  };

  const norm = (value) =>
    String(value ?? '')
      .trim()
      .toLowerCase()
      .replace(/[\s_-]+/g, ' ');

  const parseJSON = (root, selector, fallback) => {
    const node = root.querySelector(selector);
    if (!node) return fallback;
    try {
      return JSON.parse(node.textContent || '');
    } catch (error) {
      console.warn('[Suit Heritage] Could not parse customizer JSON:', selector, error);
      return fallback;
    }
  };

  const isBlank = (value) => {
    if (Array.isArray(value)) return value.length === 0;
    return value === undefined || value === null || String(value).trim() === '';
  };

  const uniqueId = (prefix = 'shc') =>
    `${prefix}-${Math.random().toString(36).slice(2, 9)}-${Date.now().toString(36)}`;

  class SuitHeritageCustomizer extends HTMLElement {
    connectedCallback() {
      if (this.dataset.shcReady === 'true') return;
      this.dataset.shcReady = 'true';

      this.abort = new AbortController();
      this.signal = this.abort.signal;

      this.productId = String(this.dataset.productId || 'product');
      this.designMode = this.dataset.designMode === 'true';
      this.customizationProfile = this.dataset.customizationProfile === 'women' ? 'women' : 'men';
      this.mode = this.dataset.mode || 'tabs';
      this.defaultForm = this.dataset.form || '3pc';
      this.formA = this.dataset.formA || this.defaultForm;
      this.formB = this.dataset.formB || 'tailoring';
      this.mtmSuitOptions = this.dataset.mtmSuitOptions || 'both';
      this.customSizeValue = this.dataset.customSizeValue || 'Custom';
      this.requireMeasurements = this.dataset.requireMeasurements === 'true';
      this.enableVest = this.dataset.enableVest === 'true';
      this.showAddons = this.dataset.showAddons === 'true';
      this.vestOptionName = this.dataset.vestOptionName || 'Vest';
      this.vestWithValue = this.dataset.vestWithValue || 'With Vest';
      this.vestWithoutValue = this.dataset.vestWithoutValue || 'Without Vest';
      this.photoLimit = Math.max(1, Number(this.dataset.photoLimit || 2));
      this.tabALabel = this.dataset.tabALabel || 'Bespoke';
      this.tabBLabel = this.dataset.tabBLabel || 'Made to Measure';
      this.enableFormPricing = this.dataset.enableFormPricing === 'true';
      this.formPriceOptionName = (this.dataset.formPriceOptionName || '').trim();
      this.bespokePriceOptionValue = (this.dataset.bespokePriceOptionValue || 'Bespoke').trim();
      this.mtmPriceOptionValue = (this.dataset.mtmPriceOptionValue || 'Made to Measure').trim();
      this.basePriceOptionValue = (this.dataset.basePriceOptionValue || 'Standard').trim();

      // R61 performance: the customizer dialog is closed on initial page load, so
      // defer parsing its schema/metaobject payload until the customer actually
      // opens it. This removes avoidable JSON parsing/index work from PDP startup.
      this.schema = null;
      this.metaRows = null;
      this.metaIndex = new Map();
      this.measureImages = null;
      this.dataHydrated = false;

      this.dialog = this.querySelector('[data-shc-dialog]');
      this.body = this.querySelector('[data-shc-body]');
      this.choicePage = this.querySelector('[data-shc-page="choice"]');
      this.formPage = this.querySelector('[data-shc-page="form"]');
      this.sectionsMount = this.querySelector('[data-shc-sections]');
      this.review = this.querySelector('[data-shc-review]');
      this.reviewList = this.querySelector('[data-shc-review-list]');
      this.error = this.querySelector('[data-shc-error]');
      this.backButton = this.querySelector('[data-shc-back]');
      this.nextButton = this.querySelector('[data-shc-next]');
      this.saveButton = this.querySelector('[data-shc-save]');
      this.wizardProgress = this.querySelector('[data-shc-wizard-progress]');
      this.wizardCounter = this.querySelector('[data-shc-wizard-counter]');
      this.wizardTitle = this.querySelector('[data-shc-wizard-title]');
      this.wizardBar = this.querySelector('[data-shc-wizard-bar]');
      this.progressChoice = this.querySelector('[data-shc-progress-choice]');
      this.progressCustomize = this.querySelector('[data-shc-progress-customize]');
      this.formKicker = this.querySelector('[data-shc-form-kicker]');
      this.formTitle = this.querySelector('[data-shc-form-title]');
      this.status = this.querySelector('[data-shc-status]');
      this.savedMark = this.querySelector('[data-shc-saved-mark]');
      this.emailPanel = this.querySelector('[data-shc-email-panel]');
      this.emailForm = this.querySelector('.shc__contact-form');
      this.emailButton = this.querySelector('[data-shc-email-send]');
      this.emailFeedback = this.querySelector('[data-shc-email-feedback]');
      this.emailSubmitKey = `shc-email-pending-v1:${location.pathname}:${this.productId}`;
      this.emailDraftKey = `shc-email-draft-v1:${location.pathname}:${this.productId}`;
      this.emailSubmitting = false;
      this.emailArmed = false;
      this.emailDefaultLabel = this.emailButton?.textContent?.trim() || 'Submit customization & email us';


      this.productRoot = this.findProductRoot();
      this.formStates = {};
      this.activeFormKey = '';
      this.activePath = '';
      this.fieldSpecs = new Map();
      this.sectionSpecs = new Map();
      this.lastTrigger = null;
      this.pendingGarment = '';
      this.wizardPages = [];
      this.currentWizardPageId = '';
      this.activeMeasurementField = '';
      this.measureGuideRefs = null;

      const remembered = MEMORY.get(this.productId);
      if (remembered) {
        this.formStates = clone(remembered.formStates || {});
        this.activeFormKey = remembered.activeFormKey || '';
        this.activePath = remembered.activePath || '';
        this.saved = clone(remembered.saved || null);
      } else {
        this.saved = null;
      }

      this.restoreEmailReturn();
      this.bind();
      this.paintSavedState();
      // A contact POST reloads the page. Rehydrate line-item properties so
      // customer's selections are not silently discarded after sending.
      if (this.saved) {
        requestAnimationFrame(() => {
          if (!this.isConnected) return;
          this.productForms().forEach((form) => this.writeProperties(form));
        });
      }

      // R61 performance: do not build the hidden form at storefront page load.
      // Keep Theme Editor behavior eager so merchants can inspect/configure the
      // block exactly as before. `open()` handles storefront hydration/rendering.
      this.activePath = this.activePath === 'b' ? 'b' : 'a';
      if (this.designMode) {
        this.hydrateData();
        if (this.mode === 'single') {
          this.renderForm(this.defaultForm, { keepState: true });
        } else {
          this.renderForm(this.activePath === 'b' ? this.formB : this.formA, {
            keepState: true
          });
        }
      }
    }

    disconnectedCallback() {
      this.abort?.abort();
      if (this.dialog?.open) {
        try {
          this.dialog.close();
        } catch (_) {}
      }
      document.body.classList.remove('shc-lock');
      this.dataset.shcReady = '';
    }

    /* ------------------------------------------------------------ setup */

    bind() {
      this.querySelectorAll('[data-shc-open]').forEach((button) => {
        button.addEventListener(
          'click',
          (event) => {
            this.lastTrigger = event.currentTarget;
            const directPath = event.currentTarget.dataset.shcOpenPath || '';
            this.open(directPath);
          },
          { signal: this.signal }
        );
      });

      this.querySelectorAll('[data-shc-close]').forEach((button) => {
        button.addEventListener('click', () => this.close(), { signal: this.signal });
      });

      this.querySelectorAll('[data-shc-path]').forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            const path = button.dataset.shcPath;
            this.choosePath(path);
          },
          { signal: this.signal }
        );
      });

      this.backButton?.addEventListener(
        'click',
        () => {
          if (!this.formPage || this.formPage.hidden) return;
          const visiblePages = this.visibleWizardPages();
          const currentIndex = visiblePages.findIndex(
            (page) => page.id === this.currentWizardPageId
          );

          if (currentIndex > 0) {
            this.goToWizardPage(visiblePages[currentIndex - 1].id);
            return;
          }

          if (this.mode === 'tabs') {
            this.close();
          }
        },
        { signal: this.signal }
      );

      this.nextButton?.addEventListener(
        'click',
        () => this.nextWizardPage(),
        { signal: this.signal }
      );

      this.saveButton?.addEventListener('click', () => this.save(), {
        signal: this.signal
      });

      // Do not intercept a valid native Shopify contact-form submission.
      // The submit event is necessary for Shopify's built-in hCaptcha.
      this.emailButton?.addEventListener('click', (event) => {
        // Reject incomplete customizations before Shopify's CAPTCHA submit hook.
        if (this.emailSubmitting || !this.validate()) event.preventDefault();
      }, { signal: this.signal, capture: true });
      this.emailForm?.addEventListener('submit', (event) => this.submitEmail(event), {
        signal: this.signal, capture: true
      });
      window.addEventListener('pageshow', (event) => {
        // Browsers restore a pending page from the back/forward cache.
        if (!event.persisted) return;
        this.emailSubmitting = false;
        if (this.emailButton) {
          this.emailButton.textContent = this.emailDefaultLabel;
          this.emailButton.removeAttribute('aria-busy');
        }
      }, { signal: this.signal });


      this.sectionsMount?.addEventListener(
        'change',
        (event) => this.handleFieldEvent(event),
        { signal: this.signal }
      );

      this.sectionsMount?.addEventListener(
        'input',
        (event) => {
          const target = event.target;
          if (!target?.matches?.('input[data-shc-measure-number], textarea')) return;
          this.handleFieldEvent(event, { lightweight: true });
        },
        { signal: this.signal }
      );

      this.sectionsMount?.addEventListener(
        'focusin',
        (event) => {
          const fieldId = event.target?.dataset?.shcInput;
          if (fieldId && MEASUREMENT_FIELD_IDS.has(fieldId)) {
            this.activateMeasurementGuide(fieldId);
          }
        },
        { signal: this.signal }
      );

      this.sectionsMount?.addEventListener(
        'click',
        (event) => {
          const help = event.target.closest?.('[data-shc-measure-help]');
          if (!help) return;
          const fieldId = help.dataset.shcMeasureHelp;
          if (!fieldId) return;
          event.preventDefault();
          this.activateMeasurementGuide(fieldId, { scrollGuide: true });
        },
        { signal: this.signal }
      );


      window.addEventListener(
        'resize',
        () => this.syncMeasurementGuidePlacement(),
        { signal: this.signal }
      );

      this.dialog?.addEventListener(
        'cancel',
        (event) => {
          event.preventDefault();
          this.close();
        },
        { signal: this.signal }
      );

      this.dialog?.addEventListener(
        'click',
        (event) => {
          /* Native dialog backdrop click lands on the dialog itself. */
          if (event.target === this.dialog) this.close();
        },
        { signal: this.signal }
      );

      document.addEventListener(
        'submit',
        (event) => {
          const form = event.target;
          if (!(form instanceof HTMLFormElement)) return;
          if (!this.isOurProductForm(form)) return;
          this.writeProperties(form);
        },
        { capture: true, signal: this.signal }
      );
    }

    findProductRoot() {
      return (
        this.closest('product-info') ||
        this.closest('[data-product-main]') ||
        this.closest('.product') ||
        this.closest('section') ||
        document
      );
    }

    productForms() {
      const scopes = [this.productRoot, document].filter(Boolean);
      const found = [];

      scopes.forEach((scope) => {
        scope.querySelectorAll?.('form[action*="/cart/add"]').forEach((form) => {
          if (found.includes(form)) return;
          if (form.closest('.shopify-payment-button')) return;
          if (!form.querySelector('[name="id"]')) return;
          found.push(form);
        });
      });

      if (this.productRoot !== document && found.length > 1) {
        const local = found.filter((form) => this.productRoot.contains(form));
        if (local.length) return local;
      }

      return found;
    }

    isOurProductForm(form) {
      if (!(form instanceof HTMLFormElement)) return false;
      if (!form.action.includes('/cart/add')) return false;
      if (this.productRoot !== document && this.productRoot.contains(form)) return true;
      return this.productForms().includes(form);
    }

    buildMetaIndex(rows) {
      const index = new Map();
      (Array.isArray(rows) ? rows : []).forEach((row) => {
        const field = String(row?.field || '').trim();
        const value = String(row?.v || '').trim();
        if (!field || !value) return;
        if (!index.has(field)) index.set(field, new Map());
        index.get(field).set(value, row);
      });
      return index;
    }

    applyWomenProfile() {
      const replacements = {
        'Chest Size': 'Bust',
        'Stomach Size': 'Waist / Stomach',
        'Pants Length': 'Trouser Length',
        'Pant Fit': 'Trouser Fit',
        'Pants only': 'Trousers only',
        'Jacket measurements': 'Jacket measurements',
        'Pant measurements': 'Trouser measurements'
      };
      Object.values(this.schema || {}).forEach((form) => {
        (form.sections || []).forEach((section) => {
          if (replacements[section.title]) section.title = replacements[section.title];
          (section.fields || []).forEach((field) => {
            if (replacements[field.label]) field.label = replacements[field.label];
            (field.options || []).forEach((option) => {
              if (replacements[option.l]) option.l = replacements[option.l];
            });
          });
        });
      });
    }

    /* ------------------------------------------------------------- dialog */

    hydrateData() {
      if (this.dataHydrated) return;
      this.schema = parseJSON(this, '[data-shc-schema]', {});
      if (this.customizationProfile === 'women') this.applyWomenProfile();
      this.metaRows = parseJSON(this, '[data-shc-metaobjects]', []);
      this.metaIndex = this.buildMetaIndex(this.metaRows);
      this.measureImages = parseJSON(this, '[data-shc-measure-images]', {});
      this.dataHydrated = true;
    }

    open(directPath = '') {
      this.hydrateData();
      if (!this.dialog) return;

      const requestedPath = directPath === 'b' ? 'b' : directPath === 'a' ? 'a' : '';
      this.directPathLaunch = requestedPath;

      if (this.mode === 'single') {
        this.renderForm(this.defaultForm, { keepState: true });
      } else if (requestedPath) {
        this.activePath = requestedPath;
        const formKey = requestedPath === 'b' ? this.formB : this.formA;
        this.renderForm(formKey, { keepState: true });
      } else if (this.saved?.activeFormKey && this.activeFormKey) {
        this.renderForm(this.activeFormKey, { keepState: true });
      } else {
        this.activePath = this.activePath === 'b' ? 'b' : 'a';
        this.renderForm(this.activePath === 'b' ? this.formB : this.formA, {
          keepState: true
        });
      }

      if (!this.dialog.open) {
        try {
          this.dialog.showModal();
        } catch (error) {
          console.warn('[Suit Heritage] Unable to open customizer dialog:', error);
          return;
        }
      }

      document.body.classList.add('shc-lock');
      requestAnimationFrame(() => {
        this.dialog.classList.add('is-in');
        const focusTarget =
          this.dialog.querySelector('.shc__path') ||
          this.dialog.querySelector('input, select, textarea, button');
        focusTarget?.focus({ preventScroll: true });
      });
    }

    close() {
      if (!this.dialog?.open) return;
      this.dialog.classList.remove('is-in');
      document.body.classList.remove('shc-lock');

      window.setTimeout(() => {
        if (!this.dialog?.open || this.dialog.classList.contains('is-in')) return;
        try {
          this.dialog.close();
        } catch (_) {}
        this.directPathLaunch = '';
        if (this.lastTrigger?.isConnected) {
          this.lastTrigger.focus({ preventScroll: true });
        }
      }, 250);
    }

    openFromSizing(detail = {}) {
      const garment = norm(detail.garment);
      this.pendingGarment = garment === 'pant' ? 'pants' : garment;
      this.lastTrigger = null;

      if (this.mode === 'tabs') {
        this.activePath = 'b';
        this.renderForm(this.formB || 'tailoring', {
          keepState: true,
          preselectGarment: this.pendingGarment
        });
      } else {
        this.renderForm(this.defaultForm, {
          keepState: true,
          preselectGarment: this.pendingGarment
        });
      }

      if (!this.dialog?.open) {
        try {
          this.dialog.showModal();
        } catch (error) {
          console.warn('[Suit Heritage] Unable to open customizer from sizing:', error);
          return false;
        }
      }

      document.body.classList.add('shc-lock');
      requestAnimationFrame(() => {
        this.dialog.classList.add('is-in');
        const target =
          this.sectionsMount?.querySelector('input:checked') ||
          this.sectionsMount?.querySelector('input, select, textarea, button');
        target?.focus({ preventScroll: true });
      });
      return true;
    }

    /* ------------------------------------------------------------- pages */

    showChoice() {
      const path = this.activePath === 'b' ? 'b' : 'a';
      this.activePath = path;
      this.renderForm(path === 'b' ? this.formB : this.formA, { keepState: true });
    }

    choosePath(path) {
      this.activePath = path === 'b' ? 'b' : 'a';
      const formKey = this.activePath === 'b' ? this.formB : this.formA;
      this.querySelectorAll('[data-shc-path]').forEach((button) => {
        button.classList.toggle('is-selected', button.dataset.shcPath === this.activePath);
      });
      this.renderForm(formKey, { keepState: true });
      this.remember();
    }

    renderForm(formKey, options = {}) {
      const schema = this.schema?.[formKey];
      if (!schema || !Array.isArray(schema.sections)) {
        this.showError('This customization form is not configured yet.');
        return false;
      }

      this.activeFormKey = formKey;
      if (!this.formStates[formKey] || !options.keepState) {
        this.formStates[formKey] = this.formStates[formKey] || {};
      }
      this.state = this.formStates[formKey];
      this.activeSchema = schema;
      this.fieldSpecs.clear();
      this.sectionSpecs.clear();
      this.sectionsMount.replaceChildren();
      this.measureGuideRefs = null;
      this.activeMeasurementField = '';

      if (options.preselectGarment && formKey === 'tailoring') {
        const allowed = ['2pc', '3pc', 'jacket', 'pants'];
        const value = allowed.includes(options.preselectGarment)
          ? options.preselectGarment
          : options.preselectGarment === 'pant'
            ? 'pants'
            : '';
        if (value) this.state.garment_type = value;
      }

      this.wizardPages = this.buildWizardPages(schema, formKey);
      this.wizardPages.forEach((page, pageIndex) => {
        if (page.type === 'review') return;
        const element = this.renderWizardPage(page, pageIndex);
        page.element = element;
        this.sectionsMount.appendChild(element);
      });

      this.applySpecialDefaults();
      this.syncAllControlsFromState();
      this.syncVisibility();
      this.paintReview();
      this.refreshWizardPages();

      if (this.choicePage) this.choicePage.hidden = true;
      if (this.formPage) this.formPage.hidden = false;

      if (this.wizardProgress) this.wizardProgress.hidden = false;
      if (this.formKicker) {
        this.formKicker.textContent =
          this.activePath === 'b' || formKey === 'tailoring'
            ? this.tabBLabel
            : this.tabALabel;
      }
      if (this.formTitle) this.formTitle.textContent = schema.title || 'Customize';

      const visible = this.visibleWizardPages();
      const preferred = visible.find((page) => page.id === options.pageId) || visible[0];
      if (preferred) this.goToWizardPage(preferred.id, { focus: false, scroll: false });

      this.clearError();
      this.remember();
      return true;
    }

    buildWizardPages(schema, formKey) {
      const pages = [];
      const byId = new Map((schema.sections || []).map((section) => [section.id, section]));

      const pageFromSection = (section) => ({
        id: `page-${section.id}`,
        title: section.title || 'Details',
        type: 'section',
        sections: [section]
      });

      if (formKey === 'tailoring') {
        const garment = byId.get('garment');
        const fitNotes = byId.get('fit_notes');
        const jacketMeasurements = byId.get('jacket_meas');
        const pantMeasurements = byId.get('pant_meas');

        /* Made to Measure stays intentionally compact:
           1) Suiting Type  2) Measurements  3) Fit & Notes.
           Jacket and pant measurements share Step 2, while section visibility
           still follows the selected garment type. */
        if (garment) pages.push(pageFromSection(garment));

        pages.push({
          id: 'page-measurements',
          title: 'Measurements',
          type: 'measurements',
          sections: [jacketMeasurements, pantMeasurements].filter(Boolean)
        });

        if (fitNotes) {
          pages.push({
            id: 'page-fit-notes',
            title: 'Fit & Notes',
            type: 'section',
            sections: [fitNotes]
          });
        }

        return pages;
      }

      const fitSection = byId.get('fit');
      (schema.sections || []).forEach((section) => {
        if (section.id === 'fit') return;
        pages.push(pageFromSection(section));
      });

      if (fitSection) {
        const sizingFields = (fitSection.fields || []).filter(
          (field) => !MEASUREMENT_FIELD_IDS.has(field.id)
        );
        const measurementFields = (fitSection.fields || []).filter((field) =>
          MEASUREMENT_FIELD_IDS.has(field.id)
        );

        pages.push({
          id: 'page-fit-sizing',
          title: 'Fit & Sizing',
          type: 'section',
          sections: [
            {
              ...fitSection,
              id: 'fit_setup',
              title: 'Fit & Sizing',
              fields: sizingFields
            }
          ]
        });

        pages.push({
          id: 'page-measurements',
          title: 'Measurements',
          type: 'measurements',
          onlyWhenTailoring: true,
          sections: [
            {
              ...fitSection,
              id: 'measurements',
              title: 'Body measurements',
              fields: measurementFields
            }
          ]
        });
      }

      pages.push({ id: 'page-review', title: 'Review', type: 'review', sections: [] });
      return pages;
    }

    renderWizardPage(page, pageIndex) {
      const element = document.createElement('section');
      element.className = 'shc__wizard-page';
      element.dataset.shcWizardPage = page.id;
      element.dataset.shcWizardType = page.type;

      if (page.type === 'measurements') {
        const layout = document.createElement('div');
        layout.className = 'shc__measurement-layout';

        const fields = document.createElement('div');
        fields.className = 'shc__measurement-fields';

        page.sections.forEach((section, index) => {
          fields.appendChild(this.renderSection(section, index, { compactHeading: true }));
        });

        layout.append(fields, this.createMeasurementGuide());
        element.appendChild(layout);
      } else {
        page.sections.forEach((section, index) => {
          element.appendChild(
            this.renderSection(section, index, {
              hideHeading: page.sections.length === 1 && section.title === page.title
            })
          );
        });
      }

      return element;
    }

    renderSection(section, sectionIndex, options = {}) {
      const sectionEl = document.createElement('section');
      sectionEl.className = 'shc__section shc__section--page';
      sectionEl.dataset.shcSection = section.id || `section-${sectionIndex + 1}`;
      this.sectionSpecs.set(sectionEl.dataset.shcSection, section);

      if (!options.hideHeading) {
        const heading = document.createElement('h5');
        heading.className = `shc__section-title ff-heading${options.compactHeading ? ' shc__section-title--compact' : ''}`;
        heading.textContent = section.title || 'Details';
        sectionEl.appendChild(heading);
      }

      const body = document.createElement('div');
      body.className = 'shc__section-body shc__section-body--page';

      (section.fields || []).forEach((field) => {
        this.fieldSpecs.set(field.id, field);
        body.appendChild(this.renderField(field));
      });

      sectionEl.appendChild(body);
      return sectionEl;
    }

    pageVisible(page) {
      if (!page) return false;
      if (page.type === 'review') return true;
      if (page.onlyWhenTailoring && this.state.customization_tier !== 'tailoring') return false;
      if (!page.element) return false;

      const sections = Array.from(page.element.querySelectorAll('[data-shc-section]'));
      return sections.some((section) => {
        if (section.hidden) return false;
        return Array.from(section.querySelectorAll('[data-shc-field]')).some(
          (field) => !field.hidden
        );
      });
    }

    visibleWizardPages() {
      return (this.wizardPages || []).filter((page) => this.pageVisible(page));
    }

    refreshWizardPages() {
      const visible = this.visibleWizardPages();
      if (!visible.length) return;

      if (!visible.some((page) => page.id === this.currentWizardPageId)) {
        this.currentWizardPageId = visible[0].id;
      }

      /* Never reveal every valid wizard page during a refresh. Validation can
         return early, so doing that would leave the full form exposed. Keep
         only the current page visible and hide every other wizard page. */
      (this.wizardPages || []).forEach((page) => {
        if (!page.element) return;
        page.element.hidden =
          page.id !== this.currentWizardPageId || !this.pageVisible(page);
      });
    }

    goToWizardPage(pageId, options = {}) {
      this.refreshWizardPages();
      const visible = this.visibleWizardPages();
      if (!visible.length) return false;

      const page = visible.find((entry) => entry.id === pageId) || visible[0];
      this.currentWizardPageId = page.id;

      (this.wizardPages || []).forEach((entry) => {
        if (entry.element) entry.element.hidden = entry.id !== page.id || !this.pageVisible(entry);
      });

      const isReview = page.type === 'review';
      if (this.review) {
        if (isReview) {
          this.paintReview();
          this.review.hidden = false;
        } else {
          this.review.hidden = true;
        }
      }

      const index = visible.findIndex((entry) => entry.id === page.id);
      const isTailoringFlow = this.activeFormKey === 'tailoring';
      const progressPages = isTailoringFlow
        ? visible.filter((entry) => entry.type !== 'review')
        : visible;
      const progressIndex = progressPages.findIndex((entry) => entry.id === page.id);
      const progressTotal = Math.max(1, progressPages.length);

      if (this.wizardCounter) {
        if (isTailoringFlow && isReview) {
          this.wizardCounter.textContent = 'Final review';
        } else {
          const stepIndex = progressIndex >= 0 ? progressIndex : index;
          const stepTotal = isTailoringFlow ? progressTotal : visible.length;
          this.wizardCounter.textContent = `Step ${stepIndex + 1} of ${stepTotal}`;
        }
      }
      if (this.wizardTitle) this.wizardTitle.textContent = page.title || 'Details';
      if (this.wizardBar) {
        if (isTailoringFlow && isReview) {
          this.wizardBar.style.width = '100%';
        } else {
          const stepIndex = progressIndex >= 0 ? progressIndex : index;
          const stepTotal = isTailoringFlow ? progressTotal : visible.length;
          this.wizardBar.style.width = `${((stepIndex + 1) / Math.max(1, stepTotal)) * 100}%`;
        }
      }

      const isFinalTailoringStep =
        isTailoringFlow &&
        !isReview &&
        progressIndex >= 0 &&
        progressIndex === progressTotal - 1;

      if (this.backButton) {
        this.backButton.hidden = index === 0 && this.mode !== 'tabs';
      }
      if (this.nextButton) this.nextButton.hidden = isReview || isFinalTailoringStep;
      if (this.saveButton) this.saveButton.hidden = !(isReview || isFinalTailoringStep);
      if (this.emailPanel) this.emailPanel.hidden = !(isReview || isFinalTailoringStep);


      this.clearError();

      if (page.type === 'measurements') {
        const mobileMeasurementGuide = window.matchMedia('(max-width: 799px)').matches;

        if (mobileMeasurementGuide) {
          this.resetMeasurementGuide();
        } else {
          const layout = page.element?.querySelector('.shc__measurement-layout');
          if (layout && this.measureGuideRefs?.root?.parentElement !== layout) {
            layout.appendChild(this.measureGuideRefs.root);
          }
          this.activeMeasurementField = '';

          const first = Array.from(
            page.element?.querySelectorAll('[data-shc-measurement-field] input[data-shc-input]') || []
          ).find(
            (input) =>
              !input.closest('[data-shc-section][hidden]') &&
              !input.closest('[data-shc-field][hidden]')
          );

          if (first?.dataset?.shcInput) {
            this.activateMeasurementGuide(first.dataset.shcInput);
          }
        }
      }

      if (options.scroll !== false) {
        this.body?.scrollTo({ top: 0, behavior: options.instant ? 'auto' : 'smooth' });
      }

      if (options.focus !== false) {
        requestAnimationFrame(() => {
          if (isReview) {
            this.review?.querySelector('h3')?.focus?.({ preventScroll: true });
            return;
          }

          if (
            page.type === 'measurements' &&
            window.matchMedia('(max-width: 799px)').matches
          ) {
            return;
          }

          const target =
            page.element?.querySelector('input:checked') ||
            page.element?.querySelector('input, select, textarea, button');
          target?.focus({ preventScroll: true });
        });
      }

      return true;
    }

    nextWizardPage() {
      this.syncVisibility();
      this.refreshWizardPages();
      const visible = this.visibleWizardPages();
      const index = visible.findIndex((page) => page.id === this.currentWizardPageId);
      if (index < 0) return;

      const current = visible[index];
      if (!this.validateWizardPage(current)) return;
      const next = visible[index + 1];
      if (next) this.goToWizardPage(next.id);
    }

    validateWizardPage(page) {
      if (!page || page.type === 'review' || !page.element) return true;
      const missing = [];
      const invalid = [];

      page.element.querySelectorAll('[data-shc-field]').forEach((element) => {
        if (element.hidden || element.closest('[data-shc-section][hidden]')) {
          return;
        }

        const fieldId = element.dataset.shcField;
        const spec = this.fieldSpecs.get(fieldId);
        if (!spec) return;

        let required = Boolean(spec.required);
        if (
          this.requireMeasurements &&
          spec.type === 'number' &&
          this.measurementRequiredForCurrentForm()
        ) {
          required = true;
        }

        const value = this.state[fieldId];
        if (spec.type === 'number' && !isBlank(value) && !validMeasurement(value)) {
          invalid.push({ id: fieldId, label: spec.label || fieldId });
        } else if (required && isBlank(value)) {
          missing.push({ id: fieldId, label: spec.label || fieldId });
        }
      });

      if (!missing.length && !invalid.length) {
        this.lastMissingFieldId = '';
        return true;
      }

      const first = invalid[0] || missing[0];
      this.lastMissingFieldId = first.id;
      this.showError(
        invalid.length
          ? `Enter a positive measurement with up to 2 digits before the decimal and 2 after for: ${invalid.slice(0, 4).map((item) => item.label).join(', ')}${invalid.length > 4 ? '…' : ''}`
          : `Please complete: ${missing.slice(0, 4).map((item) => item.label).join(', ')}${missing.length > 4 ? '…' : ''}`
      );
      if (MEASUREMENT_FIELD_IDS.has(first.id)) this.activateMeasurementGuide(first.id);
      const field = page.element.querySelector(
        `[data-shc-field="${CSS.escape(first.id)}"]`
      );
      field?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      field?.querySelector('input, select, textarea, button')?.focus({ preventScroll: true });
      return false;
    }

    findWizardPageForField(fieldId) {
      const element = this.sectionsMount?.querySelector(
        `[data-shc-field="${CSS.escape(fieldId)}"]`
      );
      return element?.closest('[data-shc-wizard-page]')?.dataset?.shcWizardPage || '';
    }

    createMeasurementGuide() {
      if (this.measureGuideRefs?.root) return this.measureGuideRefs.root;

      const guide = document.createElement('aside');
      guide.className = 'shc__measure-guide';
      guide.dataset.shcMeasureGuide = '';
      guide.dataset.shcMeasureGuideActive = 'false';

      const media = document.createElement('div');
      media.className = 'shc__measure-guide-media';

      const image = document.createElement('img');
      image.className = 'shc__measure-guide-image';
      image.alt = '';
      image.loading = 'eager';
      image.dataset.shcMeasureGuideImage = '';

      const fallback = document.createElement('div');
      fallback.className = 'shc__measure-guide-fallback';
      fallback.dataset.shcMeasureGuideFallback = '';
      fallback.innerHTML =
        '<svg viewBox="0 0 64 64" aria-hidden="true"><path d="M18 14h28v36H18zM23 20h18M23 27h12M23 34h16M23 41h10"/></svg>' +
        '<span>Measurement guide</span>';

      media.append(image, fallback);

      const copy = document.createElement('div');
      copy.className = 'shc__measure-guide-copy';

      const eyebrow = document.createElement('p');
      eyebrow.className = 'shc__measure-guide-eyebrow fs-accent';
      eyebrow.textContent = 'How to measure';

      const title = document.createElement('h5');
      title.className = 'shc__measure-guide-title ff-heading';
      title.dataset.shcMeasureGuideTitle = '';

      const steps = document.createElement('ol');
      steps.className = 'shc__measure-guide-steps';
      steps.dataset.shcMeasureGuideSteps = '';

      copy.append(eyebrow, title, steps);
      guide.append(media, copy);

      this.measureGuideRefs = { root: guide, image, fallback, title, steps };
      return guide;
    }

    resetMeasurementGuide() {
      this.activeMeasurementField = '';

      this.sectionsMount
        ?.querySelectorAll('[data-shc-measurement-field]')
        .forEach((field) => field.classList.remove('is-guide-active'));

      if (!this.measureGuideRefs?.root) return;

      const root = this.measureGuideRefs.root;
      root.dataset.shcMeasureGuideActive = 'false';
      root.classList.remove('shc__measure-guide--mobile-inline');

      /*
        Keep one stable desktop home for the guide. On mobile the guide can
        remain wherever it last appeared because the inactive state is hidden.
      */
      if (!window.matchMedia('(max-width: 799px)').matches) {
        const currentPage = this.sectionsMount?.querySelector(
          `[data-shc-wizard-page="${CSS.escape(this.currentWizardPageId || '')}"]`
        );
        const layout = currentPage?.querySelector('.shc__measurement-layout');
        const fields = layout?.querySelector('.shc__measurement-fields');

        if (layout && fields && root.parentElement !== layout) {
          layout.appendChild(root);
        }
      }
    }

    syncMeasurementGuidePlacement() {
      if (!this.measureGuideRefs?.root) return;

      const root = this.measureGuideRefs.root;
      const mobile = window.matchMedia('(max-width: 799px)').matches;

      if (!mobile) {
        root.classList.remove('shc__measure-guide--mobile-inline');

        const page = this.sectionsMount?.querySelector(
          `[data-shc-wizard-page="${CSS.escape(this.currentWizardPageId || '')}"]`
        ) || root.closest('[data-shc-wizard-page]') ||
          this.sectionsMount?.querySelector('[data-shc-wizard-type="measurements"]');

        const layout = page?.querySelector('.shc__measurement-layout');
        if (layout && root.parentElement !== layout) {
          layout.appendChild(root);
        }

        if (
          !this.activeMeasurementField &&
          page?.dataset?.shcWizardType === 'measurements'
        ) {
          const first = Array.from(
            page?.querySelectorAll(
              '[data-shc-measurement-field] input[data-shc-input]'
            ) || []
          ).find(
            (input) =>
              !input.closest('[data-shc-section][hidden]') &&
              !input.closest('[data-shc-field][hidden]')
          );

          if (first?.dataset?.shcInput) {
            this.activateMeasurementGuide(first.dataset.shcInput);
          }
        }

        return;
      }

      if (!this.activeMeasurementField) {
        root.dataset.shcMeasureGuideActive = 'false';
        return;
      }

      const field = this.sectionsMount?.querySelector(
        `[data-shc-measurement-field="${CSS.escape(
          this.activeMeasurementField
        )}"]`
      );

      if (field) {
        field.insertAdjacentElement('afterend', root);
        root.classList.add('shc__measure-guide--mobile-inline');
        root.dataset.shcMeasureGuideActive = 'true';
      }
    }

    activateMeasurementGuide(fieldId, options = {}) {
      if (!MEASUREMENT_FIELD_IDS.has(fieldId) || !this.measureGuideRefs) return;

      const guide = MEASUREMENT_GUIDES[fieldId];
      if (!guide) return;

      const mobile = window.matchMedia('(max-width: 799px)').matches;
      const root = this.measureGuideRefs.root;

      this.activeMeasurementField = fieldId;

      this.sectionsMount
        ?.querySelectorAll('[data-shc-measurement-field]')
        .forEach((field) => {
          field.classList.toggle(
            'is-guide-active',
            field.dataset.shcMeasurementField === fieldId
          );
        });

      this.measureGuideRefs.title.textContent = guide.title;
      this.measureGuideRefs.steps.replaceChildren();

      (guide.steps || []).forEach((text) => {
        const li = document.createElement('li');
        li.textContent = text;
        this.measureGuideRefs.steps.appendChild(li);
      });

      const src = String(this.measureImages?.[fieldId] || '').trim();

      if (src) {
        this.measureGuideRefs.image.src = src;
        this.measureGuideRefs.image.alt = `${guide.title} measurement guide`;
        this.measureGuideRefs.image.hidden = false;
        this.measureGuideRefs.fallback.hidden = true;
      } else {
        this.measureGuideRefs.image.removeAttribute('src');
        this.measureGuideRefs.image.hidden = true;
        this.measureGuideRefs.fallback.hidden = false;
      }

      root.dataset.shcMeasureGuideActive = 'true';

      if (mobile) {
        const field = this.sectionsMount?.querySelector(
          `[data-shc-measurement-field="${CSS.escape(fieldId)}"]`
        );

        if (field) {
          field.insertAdjacentElement('afterend', root);
          root.classList.add('shc__measure-guide--mobile-inline');
        }

        if (options.scrollGuide) {
          requestAnimationFrame(() => {
            root.scrollIntoView({
              behavior: 'smooth',
              block: 'nearest'
            });
          });
        }

        return;
      }

      root.classList.remove('shc__measure-guide--mobile-inline');

      const field = this.sectionsMount?.querySelector(
        `[data-shc-measurement-field="${CSS.escape(fieldId)}"]`
      );
      const page =
        field?.closest('[data-shc-wizard-page]') ||
        this.sectionsMount?.querySelector(
          `[data-shc-wizard-page="${CSS.escape(this.currentWizardPageId || '')}"]`
        ) ||
        root.closest('[data-shc-wizard-page]') ||
        this.sectionsMount?.querySelector('[data-shc-wizard-type="measurements"]');

      const layout = page?.querySelector('.shc__measurement-layout');

      if (layout && root.parentElement !== layout) {
        layout.appendChild(root);
      }
    }

    renderField(field) {
      if (field.type === 'radio' || field.type === 'checkbox') {
        return this.renderChoiceField(field);
      }

      const wrapper = document.createElement('div');
      wrapper.className = `shc__field shc__field--${field.type || 'text'}`;
      if (field.type === 'number') wrapper.classList.add('shc__field--number');
      wrapper.dataset.shcField = field.id;
      if (field.type === 'number' && MEASUREMENT_FIELD_IDS.has(field.id)) {
        wrapper.dataset.shcMeasurementField = field.id;
        wrapper.classList.add('shc__measurement-field');
      }

      const label = document.createElement('label');
      label.className = 'shc__label';
      const inputId = uniqueId(`shc-${field.id}`);
      label.htmlFor = inputId;
      label.innerHTML = `${this.escape(field.label || field.id)}${field.required ? ' <span class="shc__required">*</span>' : ''}`;

      if (field.type === 'number' && MEASUREMENT_FIELD_IDS.has(field.id)) {
        const labelRow = document.createElement('div');
        labelRow.className = 'shc__measurement-label-row';

        const help = document.createElement('button');
        help.type = 'button';
        help.className = 'shc__measure-help';
        help.dataset.shcMeasureHelp = field.id;
        help.textContent = 'How to measure';
        help.setAttribute('aria-label', `How to measure ${field.label || field.id}`);

        labelRow.append(label, help);
        wrapper.appendChild(labelRow);
      } else {
        wrapper.appendChild(label);
      }

      if (field.desc) wrapper.appendChild(this.description(field.desc));

      let input;
      if (field.type === 'select') {
        input = document.createElement('select');
        input.className = 'shc__select';
        const placeholder = document.createElement('option');
        placeholder.value = '';
        placeholder.textContent = 'Select';
        input.appendChild(placeholder);
        this.optionsForField(field).forEach((option) => {
          const opt = document.createElement('option');
          opt.value = option.v;
          opt.textContent = option.l || option.v;
          input.appendChild(opt);
        });
      } else if (field.type === 'textarea') {
        input = document.createElement('textarea');
        input.className = 'shc__textarea';
        input.rows = 4;
      } else if (field.type === 'upload') {
        return this.renderUploadField(field, wrapper, label);
      } else {
        input = document.createElement('input');
        input.className = 'shc__input';
        input.type = 'text';
        if (field.type === 'number') {
          input.dataset.shcMeasureNumber = 'true';
          input.inputMode = 'decimal';
          input.maxLength = 5;
          input.pattern = '[0-9]{1,2}(\\.[0-9]{1,2})?';
          input.title = 'Use up to two digits before and two after the decimal (e.g. 32.75)';
          input.autocomplete = 'off';
          input.spellcheck = false;
          input.dataset.shcLastValid = '';
        }
      }

      input.id = inputId;
      input.dataset.shcInput = field.id;
      input.name = `shc-${this.productId}-${this.activeFormKey}-${field.id}`;
      if (field.required) input.setAttribute('aria-required', 'true');
      if (field.type === 'number' && MEASUREMENT_FIELD_IDS.has(field.id)) {
        const control = document.createElement('div');
        control.className = 'shc__measurement-control';

        const unit = document.createElement('span');
        unit.className = 'shc__measurement-unit';
        unit.textContent = 'in';
        unit.setAttribute('aria-hidden', 'true');

        control.append(input, unit);
        wrapper.appendChild(control);
      } else {
        wrapper.appendChild(input);
      }

      return wrapper;
    }

    renderChoiceField(field) {
      const fieldset = document.createElement('fieldset');
      fieldset.className = `shc__field shc__field--${field.type}`;
      fieldset.dataset.shcField = field.id;

      const legend = document.createElement('legend');
      legend.className = 'shc__legend';
      legend.innerHTML = `${this.escape(field.label || field.id)}${field.required ? ' <span class="shc__required">*</span>' : ''}`;
      fieldset.appendChild(legend);
      if (field.desc) fieldset.appendChild(this.description(field.desc));

      const options = document.createElement('div');
      options.className = 'shc__options';
      const inputName = `shc-${this.productId}-${this.activeFormKey}-${field.id}`;

      this.optionsForField(field).forEach((option) => {
        const label = document.createElement('label');
        label.className = 'shc__option';

        const input = document.createElement('input');
        input.type = field.type;
        input.name = inputName;
        input.value = option.v;
        input.dataset.shcInput = field.id;
        if (field.required) input.setAttribute('aria-required', 'true');

        const card = document.createElement('span');
        card.className = 'shc__option-card';

        if (option.img) {
          const image = document.createElement('img');
          image.className = 'shc__option-image';
          image.src = option.img320 || option.img;
          image.srcset = [
            option.img320 ? `${option.img320} 320w` : '',
            option.img640 ? `${option.img640} 640w` : '',
            option.img960 ? `${option.img960} 960w` : ''
          ]
            .filter(Boolean)
            .join(', ');
          image.sizes = '(max-width: 719px) 72px, 76px';
          image.alt = '';
          image.loading = 'lazy';
          card.appendChild(image);
        }

        const copy = document.createElement('span');
        copy.textContent = option.l || option.v;
        card.appendChild(copy);
        label.append(input, card);
        options.appendChild(label);
      });

      fieldset.appendChild(options);
      return fieldset;
    }

    renderUploadField(field, wrapper, label) {
      label.removeAttribute('for');

      const email = (this.dataset.referenceEmail || 'info@suitheritage.com').trim();
      const whatsappDisplay = (
        this.dataset.referenceWhatsappDisplay || '+1 (945) 254-6266'
      ).trim();
      const whatsappNumber = (
        this.dataset.referenceWhatsappNumber || '19452546266'
      ).replace(/\D/g, '');

      const panel = document.createElement('div');
      panel.className = 'shc__reference-contact';

      const intro = document.createElement('p');
      intro.className = 'shc__reference-contact-intro';
      intro.textContent =
        'Have an inspiration or reference image for your order? After placing your order, please send it to us by email or WhatsApp.';

      const methods = document.createElement('div');
      methods.className = 'shc__reference-contact-methods';

      const emailLink = document.createElement('a');
      emailLink.className = 'shc__reference-contact-link';
      emailLink.href = `mailto:${email}?subject=${encodeURIComponent('Suit Heritage Reference Photo')}`;
      emailLink.textContent = `Email: ${email}`;

      const whatsappLink = document.createElement('a');
      whatsappLink.className = 'shc__reference-contact-link';
      whatsappLink.href = `https://wa.me/${whatsappNumber}`;
      whatsappLink.target = '_blank';
      whatsappLink.rel = 'noopener noreferrer';
      whatsappLink.textContent = `WhatsApp: ${whatsappDisplay}`;

      methods.append(emailLink, whatsappLink);

      const note = document.createElement('p');
      note.className = 'shc__reference-contact-note';
      note.textContent =
        'Please include your full name, order number, and product name so we can correctly match the image to your order.';

      panel.append(intro, methods, note);
      wrapper.appendChild(panel);
      return wrapper;
    }

    description(text) {
      const p = document.createElement('p');
      p.className = 'shc__desc';
      p.textContent = text;
      return p;
    }

    optionsForField(field) {
      let options = Array.isArray(field.options) ? clone(field.options) : [];

      if (this.activeFormKey === 'tailoring' && field.id === 'garment_type') {
        options = options.filter((option) => {
          if (option.v === '2pc') return this.mtmSuitOptions !== '3pc';
          if (option.v === '3pc') return this.mtmSuitOptions !== '2pc';
          return true;
        });
      }

      if (field.id === 'std_jacket_size' && options.length === 0) {
        options = this.standardSizes('jacket');
      }
      if (field.id === 'std_pant_size' && options.length === 0) {
        options = this.standardSizes('pants');
      }

      const overrides = this.metaIndex.get(field.id);
      if (!overrides) return options;

      const seen = new Set();
      const merged = options.map((option, index) => {
        const row = overrides.get(String(option.v));
        seen.add(String(option.v));
        return {
          ...option,
          ...(row || {}),
          l: row?.l || option.l || option.v,
          _order: Number(row?.sort ?? index)
        };
      });

      overrides.forEach((row, value) => {
        if (seen.has(String(value))) return;
        merged.push({ ...row, v: value, l: row.l || value, _order: Number(row.sort ?? 999) });
      });

      return merged.sort((a, b) => Number(a._order ?? 0) - Number(b._order ?? 0));
    }

    standardSizes(garment) {
      const rows = [];
      const add = (values, length) => {
        values.forEach((size) => {
          const formatted = sizing?.formatStandardSize
            ? sizing.formatStandardSize(size, length)
            : `${size}${length}`;
          rows.push({ v: formatted, l: formatted });
        });
      };

      const sizing = this.findSizingPanel();
      const key = garment === 'pant' ? 'pants' : garment;
      const sizingProfile = sizing?.sizingProfile || sizing?.dataset?.sizingProfile || '';
      const profilesMatch = sizingProfile === this.customizationProfile;
      const preset = profilesMatch ? sizing?.presets?.[key] : null;

      /* Only borrow the visible sizing panel's matrix when both blocks use the
         same Men/Women profile. Templates may intentionally configure these
         menus independently, so a Women customizer must never inherit Men sizes
         (or vice versa) from a mismatched sizing block. */
      if (preset) {
        ['S', 'R', 'L'].forEach((length) => add(Array.from(preset[length] || []), length));
        return rows;
      }

      /* Defensive fallback for contexts where the sizing element is not rendered. */
      if (this.customizationProfile === 'women') {
        const womenAdd = (values, length) => values.forEach((size) => {
          const formatted = length === 'S' ? `${size}P` : length === 'L' ? `${size}T` : `${size}`;
          rows.push({ v: formatted, l: formatted });
        });
        if (key === 'jacket') {
          womenAdd(['00','0','2','4','6','8','10','12','14','16'], 'S');
          womenAdd(['00','0','2','4','6','8','10','12','14','16','18'], 'R');
          womenAdd(['0','2','4','6','8','10','12','14','16','18'], 'L');
        } else {
          womenAdd(['00','0','2','4','6','8','10','12','14','16','18'], 'S');
          womenAdd(['00','0','2','4','6','8','10','12','14','16','18'], 'R');
          womenAdd(['00','0','2','4','6','8','10','12','14','16','18'], 'L');
        }
        return rows;
      }
      if (key === 'jacket') {
        add(['34','36','38','40','42','44','46','48','50','52','54','56','58','60','62','64'], 'S');
        add(['34','36','38','40','42','44','46','48','50','52','54','56','58','60','62','64'], 'R');
        add(['38','40','42','44','46','48','50','52','54','56','58','60'], 'L');
      } else {
        add(['28','30','32','34','36','38','40','42','44','46','48','50','52','54','56','58'], 'S');
        add(['28','30','32','34','36','38','40','42','44','46','48','50','52','54','56','58'], 'R');
        add(['32','34','36','38','40','42','44','46','48','50','52','54'], 'L');
      }

      return rows;
    }

    /* ------------------------------------------------------------- state */

    handleFieldEvent(event, options = {}) {
      const target = event.target;
      const fieldId = target?.dataset?.shcInput;
      if (!fieldId) return;
      const spec = this.fieldSpecs.get(fieldId);
      if (!spec) return;

      if (spec.type === 'number') {
        // Prevent extra whole digits, exponent notation, negative values, and
        // more than 2 decimal digits even when the value was pasted in.
        const candidate = target.value.replace(/,/g, '.');
        if (!MEASUREMENT_DRAFT.test(candidate)) {
          target.value = target.dataset.shcLastValid || '';
          return;
        }
        if (candidate !== target.value) target.value = candidate;
        target.dataset.shcLastValid = candidate;
      }

      if (spec.type === 'checkbox') {
        this.state[fieldId] = Array.from(
          this.sectionsMount.querySelectorAll(
            `input[type="checkbox"][data-shc-input="${CSS.escape(fieldId)}"]:checked`
          )
        ).map((input) => input.value);
      } else if (spec.type === 'upload') {
        this.state[fieldId] = Array.from(
          this.sectionsMount.querySelectorAll(
            `input[type="file"][data-shc-input="${CSS.escape(fieldId)}"]`
          )
        )
          .flatMap((input) => Array.from(input.files || []))
          .map((file) => file.name);
      } else if (target.type === 'radio') {
        if (target.checked) this.state[fieldId] = target.value;
      } else {
        this.state[fieldId] = target.value;
      }

      const autoAdvanceGarment =
        !options.lightweight &&
        this.activeFormKey === 'tailoring' &&
        fieldId === 'garment_type' &&
        target.type === 'radio' &&
        target.checked &&
        this.currentWizardPageId === 'page-garment';

      if (!options.lightweight) {
        this.syncVisibility();
        this.refreshWizardPages();
        if (this.currentWizardPageId) {
          this.goToWizardPage(this.currentWizardPageId, { focus: false, scroll: false });
        }
        this.paintReview();
        this.remember();
      }

      if (autoAdvanceGarment) {
        requestAnimationFrame(() => this.nextWizardPage());
      }

      this.clearError();
    }

    applySpecialDefaults() {
      if (
        this.activeFormKey === 'tailoring' &&
        !this.state.garment_type &&
        this.pendingGarment
      ) {
        this.state.garment_type = this.pendingGarment;
      }
    }

    syncAllControlsFromState() {
      this.fieldSpecs.forEach((spec, fieldId) => {
        const value = this.state[fieldId];
        const controls = this.sectionsMount.querySelectorAll(
          `[data-shc-input="${CSS.escape(fieldId)}"]`
        );

        controls.forEach((control) => {
          if (control.type === 'radio') {
            control.checked = String(value ?? '') === control.value;
          } else if (control.type === 'checkbox') {
            control.checked = Array.isArray(value) && value.includes(control.value);
          } else if (control.type !== 'file') {
            control.value = value ?? '';
            if (spec.type === 'number') {
              const text = String(value ?? '');
              control.dataset.shcLastValid = MEASUREMENT_DRAFT.test(text) ? text : '';
            }
          }
        });
      });
    }

    syncVisibility() {
      this.sectionsMount.querySelectorAll('[data-shc-field]').forEach((element) => {
        const fieldId = element.dataset.shcField;
        const spec = this.fieldSpecs.get(fieldId);
        element.hidden = !this.fieldVisible(spec);
      });

      this.sectionsMount.querySelectorAll('[data-shc-section]').forEach((element) => {
        const section = this.sectionSpecs.get(element.dataset.shcSection);
        element.hidden = !this.sectionVisible(section);
      });
    }

    fieldVisible(field) {
      if (!field) return true;
      return this.conditionSatisfied(field.cond);
    }

    sectionVisible(section) {
      if (!section) return true;
      if (!this.conditionSatisfied(section.cond)) return false;

      if (section.id === 'addons' && !this.showAddons) return false;

      if (
        this.activeFormKey === '2pc' &&
        !this.enableVest &&
        (section.id === 'vest_gate' || section.id === 'waistcoat')
      ) {
        return false;
      }

      if (this.activeFormKey === 'tailoring') {
        const garment = this.state.garment_type || this.pendingGarment || '2pc';
        if (section.id === 'jacket_meas') {
          return ['2pc', '3pc', 'jacket'].includes(garment);
        }
        if (section.id === 'pant_meas') {
          return ['2pc', '3pc', 'pants'].includes(garment);
        }
      }

      return true;
    }

    conditionSatisfied(cond) {
      if (!cond?.f) return true;
      const current = this.state[cond.f];
      if (Array.isArray(current)) return current.includes(cond.v);
      return String(current ?? '') === String(cond.v ?? '');
    }

    remember() {
      MEMORY.set(this.productId, {
        formStates: clone(this.formStates),
        activeFormKey: this.activeFormKey,
        activePath: this.activePath,
        saved: clone(this.saved), customerName: name, customerEmail: email
      });
    }

    /* ---------------------------------------------------------- validation */

    validate() {
      this.syncVisibility();
      this.refreshWizardPages();
      const pages = this.visibleWizardPages().filter((page) => page.type !== 'review');

      for (const page of pages) {
        const previous = this.currentWizardPageId;
        if (!this.validateWizardPage(page)) {
          this.goToWizardPage(page.id, { focus: false, scroll: false });
          const firstMissing = this.lastMissingFieldId
            ? page.element?.querySelector(
                `[data-shc-field="${CSS.escape(this.lastMissingFieldId)}"]`
              )
            : null;
          firstMissing?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          firstMissing?.querySelector('input, select, textarea, button')?.focus({ preventScroll: true });
          return false;
        }
        this.currentWizardPageId = previous;
      }

      return true;
    }

    measurementRequiredForCurrentForm() {
      if (this.activeFormKey === 'tailoring') return true;
      return this.state.customization_tier === 'tailoring';
    }

    findFieldIdByLabel(label) {
      for (const [id, spec] of this.fieldSpecs.entries()) {
        if ((spec.label || id) === label) return id;
      }
      return '';
    }

    /* --------------------------------------------------------------- save */

    madeToMeasureGarments(sizing = null) {
      const available = Array.isArray(sizing?.garments) && sizing.garments.length
        ? sizing.garments
        : ['jacket', 'pants'];

      if (this.activeFormKey === 'tailoring') {
        const garment = norm(this.state.garment_type || this.pendingGarment || '2pc');
        if (garment === 'jacket') return available.filter((item) => item === 'jacket');
        if (garment === 'pants' || garment === 'pant') {
          return available.filter((item) => item === 'pants');
        }
      }

      /* Full Customization + Made to Measure applies to the suit pieces present. */
      return available.filter((item) => item === 'jacket' || item === 'pants');
    }

    save(options = {}) {
      this.syncVisibility();
      if (!this.validate()) return false;

      const isTailoring = this.isMadeToMeasure();
      const sizing = this.findSizingPanel();

      if (isTailoring) {
        if (sizing && typeof sizing.applyCustomSize === 'function') {
          const ok = sizing.applyCustomSize(this.madeToMeasureGarments(sizing));
          if (ok === false) {
            this.showError('Custom sizing could not be matched to this product. Please review the product size setup.');
            return false;
          }
        } else {
          document.dispatchEvent(
            new CustomEvent('sh-size:apply-custom', {
              bubbles: true,
              detail: {
                productId: this.productId,
                garments: this.madeToMeasureGarments(null)
              }
            })
          );
        }
      } else {
        const standard = {
          jacket: this.state.std_jacket_size || '',
          pants: this.state.std_pant_size || ''
        };

        if (sizing && typeof sizing.clearCustomSize === 'function') {
          sizing.clearCustomSize();
        }

        if ((standard.jacket || standard.pants) && sizing) {
          const ok = sizing.applyExternalSelections?.(standard);
          if (ok === false) {
            this.showError('Those standard sizes are not available together for this product. Choose another combination.');
            return false;
          }
        }
      }

      if (!this.syncFormPricingOption()) return false;
      this.syncVestOption();

      const entries = this.collectEntries();
      this.saved = {
        activeFormKey: this.activeFormKey,
        activePath: this.activePath,
        isTailoring,
        pricingValue: this.pricingValueForCurrentForm(),
        entries,
        state: clone(this.state)
      };

      this.productForms().forEach((form) => this.writeProperties(form));
      this.paintReview();
      this.paintSavedState();
      this.remember();
      this.clearError();
      if (options.close !== false) this.close();
      return true;
    }

    // Email and cart-save use exactly the same verified customer selections.
    // Contact form posts are native: never use fetch() or form.submit(), which
    // can bypass Shopify's hCaptcha/challenge and yield false success screens.
    submitEmail(event) {
      if (this.emailSubmitting || !this.emailForm || !this.emailButton) {
        event.preventDefault();
        return;
      }
      // Guard against hidden form submission from another control or stale UI.
      if (this.emailPanel?.hidden || !this.dialog?.open) {
        event.preventDefault();
        return;
      }
      if (!this.emailForm.checkValidity()) {
        event.preventDefault();
        this.emailForm.reportValidity();
        return;
      }
      // Save is validated before constructing the email. It also preserves
      // line-item properties, price and size selections for subsequent checkout.
      if (!this.save({ close: false })) {
        event.preventDefault();
        return;
      }
      const name = this.emailForm.elements.namedItem('contact[name]')?.value.trim() || '';
      const email = this.emailForm.elements.namedItem('contact[email]')?.value.trim() || '';
      if (!name || !email || !this.saved?.entries?.length) {
        event.preventDefault();
        this.showError('Please provide your name, email and customization selections.');
        return;
      }
      const submissionId = uniqueId('SH-PRODUCT');
      this.querySelector('[data-shc-email-id]').value = submissionId;
      this.querySelector('[data-shc-email-body]').value = this.emailBody(submissionId, name, email);
      this.writeEmailSession(this.emailSubmitKey, { at: Date.now(), submissionId });
      this.writeEmailSession(this.emailDraftKey, {
        at: Date.now(), formStates: clone(this.formStates),
        activePath: this.activePath, activeFormKey: this.activeFormKey,
        saved: clone(this.saved)
      });
      this.emailSubmitting = true;
      this.emailButton.setAttribute('aria-busy', 'true');
      this.emailButton.textContent = 'Submitting…';
      // Shopify handles any required challenge through its native form flow.
    }

    emailBody(submissionId, name, email) {
      const lines = [
        'SUIT HERITAGE — PRODUCT CUSTOMIZATION SUBMISSION',
        '', `Submission ID: ${submissionId}`,
        `Submitted: ${new Date().toISOString()}`,
        `Customer: ${name}`,
        `Email: ${email}`,
        `Product: ${this.dataset.productTitle || ''}`,
        `Product URL: ${this.dataset.productUrl || location.href}`,
        `Service: ${this.saved.isTailoring ? 'Made to Measure' : 'Bespoke / standard size'}`,
        `Customization form: ${this.activeSchema?.title || this.saved.activeFormKey || ''}`,
        ...(this.saved.pricingValue ? [`Pricing tier: ${this.saved.pricingValue}`] : []),
        '', 'CUSTOMIZATION DETAILS'
      ];
      this.saved.entries.forEach((entry) => {
        const units = MEASUREMENT_FIELD_IDS.has(entry.id) ? ' in' : '';
        lines.push(`${entry.label}: ${entry.value}${units}`);
      });
      lines.push('', 'Pre-purchase customization request only — no order placed.',
        'Reference images, if any, must be sent separately.');
      return lines.join('\n');
    }

    writeEmailSession(key, value) {
      try { sessionStorage.setItem(key, JSON.stringify(value)); }
      catch (_) { /* Browser storage unavailable; server POST still works. */ }
    }

    readEmailSession(key) {
      try { return JSON.parse(sessionStorage.getItem(key) || 'null'); }
      catch (_) { return null; }
    }

    removeEmailSession(key) {
      try { sessionStorage.removeItem(key); } catch (_) {}
    }

    restoreEmailReturn() {
      const pending = this.readEmailSession(this.emailSubmitKey);
      const draft = this.readEmailSession(this.emailDraftKey);
      this.removeEmailSession(this.emailSubmitKey);
      this.removeEmailSession(this.emailDraftKey);
      const recent = (value) => value && Number.isFinite(Number(value.at)) &&
        Date.now() >= Number(value.at) && Date.now() - Number(value.at) < 30 * 60 * 1000;
      if (recent(draft) && draft.saved) {
        this.formStates = clone(draft.formStates || {});
        this.activeFormKey = draft.activeFormKey || '';
        this.activePath = draft.activePath || 'a';
        this.saved = clone(draft.saved);
        const nameField = this.emailForm?.elements?.namedItem('contact[name]');
        const emailField = this.emailForm?.elements?.namedItem('contact[email]');
        if (nameField && !nameField.value) nameField.value = draft.customerName || '';
        if (emailField && !emailField.value) emailField.value = draft.customerEmail || '';
      }
      if (!recent(pending) || !this.emailFeedback) return;
      if (this.querySelector('[data-shc-email-server-success]')) {
        this.emailFeedback.textContent = `Customization submitted to Suit Heritage. Reference: ${pending.submissionId}. To purchase, review your selections and add to cart.`;
        this.emailFeedback.hidden = false;
      } else {
        this.emailFeedback.textContent = 'We could not confirm that the customization email was received. Your selections have been kept here; please reopen the customizer to review or retry.';
        this.emailFeedback.hidden = false;
      }
      // Shopify may include ?contact_posted=true on the redirected URL. Clear
      // it only after matching a recent local submission, avoiding stale success.
      if (this.querySelector('[data-shc-email-server-success]')) {
        try {
          const url = new URL(location.href);
          if (url.searchParams.has('contact_posted')) {
            url.searchParams.delete('contact_posted');
            history.replaceState(history.state, '', url.pathname + url.search + url.hash);
          }
        } catch (_) {}
      }
    }

    isMadeToMeasure() {
      if (this.activeFormKey === 'tailoring') return true;
      return this.state.customization_tier === 'tailoring';
    }

    findSizingPanel() {
      const local = this.productRoot?.querySelector?.('sh-size[data-shz], sh-size.shz');
      if (local) return local;

      const productId = String(this.productId || '');
      if (productId) {
        const exact = Array.from(document.querySelectorAll('sh-size[data-product-id]')).find(
          (panel) => String(panel.dataset.productId || '') === productId
        );
        if (exact) return exact;
      }

      return document.querySelector('sh-size[data-shz], sh-size.shz') || null;
    }

    pricingValueForCurrentForm() {
      if (!this.enableFormPricing || !this.formPriceOptionName) return '';
      if (this.mode === 'tabs') {
        return this.activePath === 'b'
          ? this.mtmPriceOptionValue
          : this.bespokePriceOptionValue;
      }
      return this.activeFormKey === 'tailoring'
        ? this.mtmPriceOptionValue
        : this.bespokePriceOptionValue;
    }

    syncFormPricingOption() {
      const value = this.pricingValueForCurrentForm();
      if (!value) return true;
      const ok = this.setNativeProductOption(this.formPriceOptionName, value);
      if (!ok) {
        this.showError(
          `Form pricing is enabled, but the Shopify option "${this.formPriceOptionName}" does not contain the value "${value}" for this product.`
        );
        return false;
      }
      return true;
    }

    syncVestOption() {
      if (!this.enableVest) return;
      const wantsVest = Array.isArray(this.state.add_vest) && this.state.add_vest.includes('yes');
      const value = wantsVest ? this.vestWithValue : this.vestWithoutValue;
      this.setNativeProductOption(this.vestOptionName, value);
    }

    setNativeProductOption(name, value) {
      if (!name || !value) return false;
      const targetName = norm(name);
      const scope = this.productRoot || document;
      const fields = Array.from(
        scope.querySelectorAll(
          'select[name^="options["], input[type="radio"][name^="options["]'
        )
      );

      const relevant = fields.filter((field) => {
        const match = field.name.match(/^options\[(.*)\]$/);
        return match && norm(match[1]) === targetName;
      });

      const radio = relevant.find(
        (field) => field.type === 'radio' && norm(field.value) === norm(value)
      );
      if (radio) {
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
        return true;
      }

      const select = relevant.find((field) => field.tagName === 'SELECT');
      if (select) {
        const option = Array.from(select.options).find(
          (entry) => norm(entry.value) === norm(value) || norm(entry.textContent) === norm(value)
        );
        if (!option) return false;
        select.value = option.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        return true;
      }
      return false;
    }

    collectEntries() {
      const entries = [];
      const used = new Set();

      this.activeSchema?.sections?.forEach((section) => {
        const sectionEl = this.sectionsMount.querySelector(
          `[data-shc-section="${CSS.escape(section.id)}"]`
        );
        if (sectionEl?.hidden) return;

        (section.fields || []).forEach((field) => {
          const fieldEl = this.sectionsMount.querySelector(
            `[data-shc-field="${CSS.escape(field.id)}"]`
          );
          if (fieldEl?.hidden) return;
          if (used.has(field.id)) return;
          used.add(field.id);

          const raw = this.state[field.id];
          if (isBlank(raw)) return;

          entries.push({
            id: field.id,
            label: field.label || field.id,
            value: this.displayValue(field, raw)
          });
        });
      });

      return entries;
    }

    displayValue(field, raw) {
      if (field.type === 'checkbox') {
        const values = Array.isArray(raw) ? raw : [raw];
        return values
          .map((value) => this.optionLabel(field, value))
          .filter(Boolean)
          .join(', ');
      }
      if (field.type === 'radio' || field.type === 'select') {
        return this.optionLabel(field, raw) || String(raw);
      }
      if (field.type === 'upload') {
        return Array.isArray(raw) ? raw.join(', ') : String(raw);
      }
      return String(raw);
    }

    optionLabel(field, value) {
      return (
        this.optionsForField(field).find((option) => String(option.v) === String(value))?.l ||
        ''
      );
    }

    writeProperties(form) {
      if (!(form instanceof HTMLFormElement)) return;
      form.querySelectorAll('input[type="hidden"][data-shc-owned]').forEach((input) => input.remove());

      if (!this.saved) return;

      const properties = [
        ['Customization Form', this.activeSchema?.title || this.saved.activeFormKey || 'Suit Heritage'],
        ['Sizing Method', this.saved.isTailoring ? 'Made to measure' : 'Standard size'],
        ...(this.saved.pricingValue ? [['Pricing Tier', this.saved.pricingValue]] : []),
        ...this.saved.entries.map((entry) => [
          MEASUREMENT_FIELD_IDS.has(entry.id) ? `${entry.label} (in)` : entry.label,
          entry.value
        ])
      ];

      properties.forEach(([label, value]) => {
        if (isBlank(value)) return;
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = `properties[${PROPERTY_PREFIX}${label}]`;
        input.value = String(value);
        input.dataset[OWNED] = 'true';
        form.appendChild(input);
      });
    }

    /* ------------------------------------------------------------- review */

    paintReview() {
      if (!this.review || !this.reviewList) return;
      const entries = this.collectEntries();
      this.reviewList.replaceChildren();

      entries.forEach((entry) => {
        const dt = document.createElement('dt');
        dt.textContent = entry.label;
        const dd = document.createElement('dd');
        dd.textContent = entry.value;
        this.reviewList.append(dt, dd);
      });

      this.review.hidden = entries.length === 0;
    }

    paintSavedState() {
      const hasSaved = Boolean(this.saved?.entries?.length || this.saved?.isTailoring);
      if (this.savedMark) this.savedMark.hidden = !hasSaved;
      if (this.status) this.status.hidden = !hasSaved;
    }

    /* -------------------------------------------------------------- errors */

    showError(message) {
      if (!this.error) return;
      this.error.textContent = message;
      this.error.hidden = false;
      this.error.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    clearError() {
      if (!this.error) return;
      this.error.textContent = '';
      this.error.hidden = true;
    }

    escape(value) {
      return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    }
  }

  customElements.define('sh-customizer', SuitHeritageCustomizer);
})();
